import React from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import {
  OktonAccessDenied,
  OktonAppShell,
  OktonAuthCallback,
  OktonAuthProvider,
  OktonForgotPassword,
  OktonLogin,
  OktonPlatformProvider,
  createHttpPlatformClient,
  useOktonAuth,
  OktonLoadingState,
} from "@okton/platform-ui";

import { DashboardPage } from "./pages/DashboardPage";
import { SystemsPage } from "./pages/SystemsPage";
import { SessionsPage } from "./pages/SessionsPage";
import { UsersPage } from "./pages/UsersPage";
import { CompaniesPage } from "./pages/CompaniesPage";
import { PlansPage } from "./pages/PlansPage";
import { AiPlansPortal } from "./features/ai-plans/AiPlansPortal";
import { CommercialPage } from "./pages/CommercialPage";
import { LicensesPage } from "./pages/LicensesPage";
import { EventsPage } from "./pages/EventsPage";
import { AuditPage } from "./pages/AuditPage";
import { SettingsPage } from "./pages/SettingsPage";
import { PLATFORM_BASE_URL } from "./platform/config";

const system = { id: "sso-admin", name: "SSO Admin" };
const aiPlansSystem = { id: "ai-plans", name: "Planos de IA" };

const platformClient = createHttpPlatformClient({
  baseUrl: PLATFORM_BASE_URL,
  systemId: system.id,
});

const aiPlansPlatformClient = createHttpPlatformClient({
  baseUrl: PLATFORM_BASE_URL,
  systemId: aiPlansSystem.id,
});

const LoadingScreen: React.FC<{ message?: string }> = ({
  message = "Carregando acesso...",
}) => <OktonLoadingState title={message} minH="100vh" />;

const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useOktonAuth();
  if (isLoading) return <LoadingScreen />;
  if (isAuthenticated) return <Redirect to="/" />;
  return <>{children}</>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading, accessToken, logout } = useOktonAuth();
  const location = useLocation();
  const [accessState, setAccessState] = React.useState<
    "checking" | "allowed" | "denied"
  >("checking");

  React.useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      setAccessState("checking");
      return;
    }

    let active = true;
    const checkAccess = platformClient.checkSystemAccess;

    if (!checkAccess) {
      setAccessState("denied");
      return undefined;
    }

    checkAccess(system.id)
      .then((allowed) => {
        if (active) setAccessState(allowed ? "allowed" : "denied");
      })
      .catch(() => {
        if (active) setAccessState("denied");
      });

    return () => {
      active = false;
    };
  }, [isAuthenticated, accessToken]);

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) {
    return (
      <Redirect
        to={{ pathname: "/login", state: { from: location.pathname } }}
      />
    );
  }
  if (accessState === "checking")
    return <LoadingScreen message="Validando permissões..." />;
  if (accessState === "denied") {
    return (
      <OktonAccessDenied
        minH="100vh"
        title="Acesso não autorizado"
        description="Seu usuário não possui acesso ao SSO Admin nesta empresa/filial."
        actionLabel="Sair"
        onAction={() => logout()}
      />
    );
  }
  return <>{children}</>;
};

const ApplicationContent: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { user, bootstrap, logout } = useOktonAuth();

  if (!user) return <LoadingScreen message="Carregando usuário..." />;

  const features = {
    billing: Boolean(bootstrap?.features.billing),
    profile: bootstrap?.features.profile !== false,
    branchSwitching: Boolean(bootstrap?.features.branchSwitching),
    notifications: Boolean(bootstrap?.features.notifications),
  };

  return (
    <OktonPlatformProvider
      system={system}
      user={user}
      features={features}
      client={platformClient}
    >
      <OktonAppShell
        onLogout={() => logout()}
        pathname={location.pathname}
        onNavigate={(href) => history.push(href)}
      >
        <Switch>
          <Route exact path="/">
            <DashboardPage />
          </Route>
          <Route path="/systems">
            <SystemsPage />
          </Route>
          <Route path="/sessions">
            <SessionsPage />
          </Route>
          <Route path="/users">
            <UsersPage />
          </Route>
          <Route path="/companies">
            <CompaniesPage />
          </Route>
          <Route path="/plans">
            <PlansPage />
          </Route>
          <Route path="/commercial">
            <CommercialPage />
          </Route>
          <Route path="/licenses">
            <LicensesPage />
          </Route>
          <Route path="/events">
            <EventsPage />
          </Route>
          <Route path="/audit">
            <AuditPage />
          </Route>
          <Route path="/settings">
            <SettingsPage />
          </Route>
          <Redirect to="/" />
        </Switch>
      </OktonAppShell>
    </OktonPlatformProvider>
  );
};

const AdminApp: React.FC = () => (
  <OktonAuthProvider systemId={system.id} platformBaseUrl={PLATFORM_BASE_URL}>
    <Switch>
      <Route exact path="/auth/callback">
        <OktonAuthCallback />
      </Route>
      <Route exact path="/login">
        <PublicOnlyRoute>
          <OktonLogin />
        </PublicOnlyRoute>
      </Route>
      <Route exact path="/login-preview">
        <PublicOnlyRoute>
          <OktonLogin />
        </PublicOnlyRoute>
      </Route>
      <Route exact path="/forgot">
        <PublicOnlyRoute>
          <OktonForgotPassword />
        </PublicOnlyRoute>
      </Route>
      <Route>
        <ProtectedRoute>
          <ApplicationContent />
        </ProtectedRoute>
      </Route>
    </Switch>
  </OktonAuthProvider>
);

export const App: React.FC = () => {
  const location = useLocation();
  if (
    location.pathname === "/ai-plans" ||
    location.pathname.startsWith("/ai-plans/")
  ) {
    return (
      <OktonAuthProvider
        key="ai-plans"
        systemId="ai-plans"
        platformBaseUrl={PLATFORM_BASE_URL}
      >
        <OktonPlatformProvider
          system={aiPlansSystem}
          client={aiPlansPlatformClient}
        >
          <AiPlansPortal />
        </OktonPlatformProvider>
      </OktonAuthProvider>
    );
  }
  return <AdminApp />;
};
